import type { RouteLocationNormalizedLoaded } from "vue-router";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import {
  redirectToAuthenticatedMeterReportIfPermitted,
  redirectToUnauthenticatedMeterReportIfNotPermitted,
} from "@/app/fbs-hub-app/shared/helper/meterReportRedirect.helper";
import {
  CHECKOUT_SOURCE_ELEMENT_QUERY,
  CHECKOUT_SOURCE_PAGE_QUERY,
} from "@/app/fbs-hub-app/shared/tracking/helper/constants.helper";

const StartView = () =>
  import("@/app/fbs-hub-app/start/StartView/StartView.vue");
const CheckoutView = () =>
  import("@/app/fbs-hub-app/checkout/CheckoutView/CheckoutView.vue");
const GasContainersView = () =>
  import("@/app/fbs-hub-app/gas-containers/GasContainersView/GasContainersView.vue");
const WestfalengasBioView = () =>
  import("@/app/fbs-hub-app/westfalengas-bio/WestfalengasBioView/WestfalengasBioView.vue");
const WestfalengasBioTariffView = () =>
  import("@/app/fbs-hub-app/westfalengas-bio/tariff/TariffView/TariffView.vue");
const PriceCockpitView = () =>
  import("@/app/fbs-hub-app/price-cockpit/PriceCockpitView/PriceCockpitView.vue");
const MeterReportView = () =>
  import("@/app/fbs-hub-app/meter-report/MeterReportView/MeterReportView.vue");
const UnauthenticatedMeterReportView = () =>
  import("@/app/fbs-hub-app/unauthenticated-meter-report/MeterReportView/MeterReportView.vue");
const AutogasMeterReportView = () =>
  import("@/app/fbs-hub-app/autogas-meter-report/AutogasMeterReportView/AutogasMeterReportView.vue");
const MonthlyPaymentView = () =>
  import("@/app/fbs-hub-app/monthly-payment/MonthlyPaymentView/MonthlyPaymentView.vue");
const OrdersView = () =>
  import("@/app/fbs-hub-app/orders/OrdersView/OrdersView.vue");
const DocumentsView = () =>
  import("@/app/fbs-hub-app/documents/DocumentsView/DocumentsView.vue");
const AddCustomerView = () =>
  import("@/app/fbs-hub-app/add-customer/AddCustomerView/AddCustomerView.vue");
const PageNotFoundView = () =>
  import("@/shared/views/PageNotFoundView/PageNotFoundView.vue");

export const routes = [
  {
    path: "",
    redirect: { name: AppRouteNames.FbsHubStart },
  },
  {
    path: "start",
    name: AppRouteNames.FbsHubStart,
    meta: {
      titleKey: "fbsHub.start.title",
    },
    component: StartView,
  },
  {
    path: "checkout",
    name: AppRouteNames.FbsHubCheckout,
    meta: {
      titleKey: "fbsHub.checkout.title",
    },
    props: (route: RouteLocationNormalizedLoaded) => ({
      sourceRouteName: route.query[CHECKOUT_SOURCE_PAGE_QUERY],
      sourceElement: route.query[CHECKOUT_SOURCE_ELEMENT_QUERY],
    }),
    component: CheckoutView,
  },
  {
    path: "gas-containers",
    name: AppRouteNames.FbsHubGasContainers,
    meta: {
      titleKey: "fbsHub.gasContainers.title",
    },
    component: GasContainersView,
  },
  {
    path: "westfalengas-bio",
    name: AppRouteNames.FbsHubWestfalengasBio,
    meta: {
      titleKey: "fbsHub.westfalengasBio.title",
    },
    component: WestfalengasBioView,
  },
  {
    path: "westfalengas-bio/:tariffId/:containerSerialNumber",
    name: AppRouteNames.FbsHubWestfalengasBioTariff,
    component: WestfalengasBioTariffView,
    props: true,
  },
  {
    path: "price-cockpit",
    name: AppRouteNames.FbsHubPriceCockpit,
    meta: {
      titleKey: "fbsHub.priceCockpit.title",
    },
    component: PriceCockpitView,
  },
  {
    path: "meter-report",
    name: AppRouteNames.FbsHubMeterReport,
    meta: {
      titleKey: "fbsHub.meterReport.title",
    },
    component: MeterReportView,
    beforeEnter: redirectToUnauthenticatedMeterReportIfNotPermitted,
  },
  {
    path: "/meter-report",
    name: AppRouteNames.MeterReport,
    meta: {
      titleKey: "fbsHub.meterReport.title",
    },
    component: UnauthenticatedMeterReportView,
    beforeEnter: redirectToAuthenticatedMeterReportIfPermitted,
  },
  {
    path: "autogas-meter-report",
    name: AppRouteNames.FbsHubAutogasMeterReport,
    meta: {
      titleKey: "fbsHub.autogasMeterReport.title",
    },
    component: AutogasMeterReportView,
  },
  {
    path: "monthly-payment",
    name: AppRouteNames.FbsHubMonthlyPayment,
    meta: {
      titleKey: "fbsHub.monthlyPayment.title",
    },
    component: MonthlyPaymentView,
  },
  {
    path: "orders",
    name: AppRouteNames.FbsHubOrders,
    meta: {
      titleKey: "shared.term.order.myOrders",
    },
    component: OrdersView,
  },
  {
    path: "documents",
    name: AppRouteNames.FbsHubDocuments,
    meta: {
      titleKey: "fbsHub.documents.title",
    },
    component: DocumentsView,
  },
  {
    path: "add-customer",
    name: AppRouteNames.FbsHubAddCustomer,
    meta: {
      titleKey: "shared.addCustomer.title",
    },
    component: AddCustomerView,
  },
  {
    path: ":pathMatch(.*)*",
    name: AppRouteNames.FbsHubNotFound,
    meta: {
      titleKey: "shared.error.page.notFoundError.title",
    },
    component: PageNotFoundView,
    props: {
      homeRouteName: AppRouteNames.FbsHubStart,
    },
  },
];
